import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface Postfach {
  id: number;
  bezeichnung: string;
  provider: 'imap' | 'graph';
  host: string | null;
  port: number | null;
  benutzer: string | null;
  passwort: string; // "***" wenn gesetzt, "" wenn leer
  ordner: string | null;
  aktiv: boolean;
  letzte_pruefung: string | null;
  letzter_status: string | null;
  erstellt_am: string | null;
}

interface PostfachForm {
  bezeichnung: string;
  provider: 'imap' | 'graph';
  host: string;
  port: string;
  benutzer: string;
  passwort: string;
  ordner: string;
  aktiv: boolean;
}

interface Props {
  authToken: string;
  istOffen: boolean;
  onToggle: () => void;
}

const LEERES_FORMULAR: PostfachForm = {
  bezeichnung: '',
  provider: 'imap',
  host: '',
  port: '993',
  benutzer: '',
  passwort: '',
  ordner: 'INBOX',
  aktiv: true,
};

const PostfachSettings: React.FC<Props> = ({ authToken, istOffen, onToggle }) => {
  const [postfaecher, setPostfaecher] = useState<Postfach[]>([]);
  const [loading, setLoading] = useState(true);
  const [ladeError, setLadeError] = useState<string | null>(null);

  // Formular (Anlegen / Bearbeiten)
  const [formOffen, setFormOffen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<PostfachForm>(LEERES_FORMULAR);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Verbindungstest / Toggle-Feedback pro Postfach
  const [testingId, setTestingId] = useState<number | null>(null);
  const [testErgebnis, setTestErgebnis] = useState<Record<number, { ok: boolean; text: string }>>({});
  const [aktionError, setAktionError] = useState<string | null>(null);

  const headers = { Authorization: `Bearer ${authToken}` };

  const load = async () => {
    setLoading(true);
    setLadeError(null);
    try {
      const res = await axios.get(`${API_URL}/api/postfaecher`, { headers });
      setPostfaecher(res.data);
    } catch (e: any) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      setLadeError(
        status === 403
          ? 'Kein Zugriff – nur Admins können die Postfächer sehen.'
          : detail || `Fehler beim Laden (HTTP ${status || 'unbekannt'})`
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [authToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleForm = (feld: keyof PostfachForm, wert: string | boolean) => {
    setForm(prev => {
      const next = { ...prev, [feld]: wert } as PostfachForm;
      // Beim Provider-Wechsel sinnvolle Ordner-Defaults setzen, solange der User nichts Eigenes eingetragen hat
      if (feld === 'provider') {
        if (wert === 'graph' && (prev.ordner === 'INBOX' || prev.ordner === '')) next.ordner = 'inbox';
        if (wert === 'imap' && (prev.ordner === 'inbox' || prev.ordner === '')) next.ordner = 'INBOX';
      }
      return next;
    });
  };

  const neuesPostfach = () => {
    setEditId(null);
    setForm(LEERES_FORMULAR);
    setFormError(null);
    setFormOffen(true);
  };

  const bearbeiten = (p: Postfach) => {
    setEditId(p.id);
    setForm({
      bezeichnung: p.bezeichnung,
      provider: p.provider,
      host: p.host || '',
      port: p.port != null ? String(p.port) : '993',
      benutzer: p.benutzer || '',
      passwort: p.passwort, // "***" = unverändert lassen
      ordner: p.ordner || (p.provider === 'graph' ? 'inbox' : 'INBOX'),
      aktiv: p.aktiv,
    });
    setFormError(null);
    setFormOffen(true);
  };

  const formularSchliessen = () => {
    setFormOffen(false);
    setEditId(null);
    setForm(LEERES_FORMULAR);
    setFormError(null);
  };

  const speichern = async () => {
    if (!form.bezeichnung.trim()) {
      setFormError('Bitte eine Bezeichnung angeben.');
      return;
    }
    if (form.provider === 'imap' && (!form.host.trim() || !form.benutzer.trim())) {
      setFormError('Für IMAP sind Host und Benutzer erforderlich.');
      return;
    }
    if (form.provider === 'graph' && !form.benutzer.trim()) {
      setFormError('Bitte die Postfach-Adresse angeben.');
      return;
    }

    const body = {
      bezeichnung: form.bezeichnung.trim(),
      provider: form.provider,
      host: form.provider === 'imap' ? form.host.trim() : null,
      port: form.provider === 'imap' ? (parseInt(form.port, 10) || 993) : null,
      benutzer: form.benutzer.trim(),
      passwort: form.passwort,
      ordner: form.ordner.trim() || (form.provider === 'graph' ? 'inbox' : 'INBOX'),
      aktiv: form.aktiv,
    };

    setSaving(true);
    setFormError(null);
    try {
      if (editId != null) {
        await axios.put(`${API_URL}/api/postfaecher/${editId}`, body, { headers });
      } else {
        await axios.post(`${API_URL}/api/postfaecher`, body, { headers });
      }
      formularSchliessen();
      load();
    } catch (e: any) {
      setFormError(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const toggleAktiv = async (p: Postfach) => {
    setAktionError(null);
    try {
      await axios.put(
        `${API_URL}/api/postfaecher/${p.id}`,
        {
          bezeichnung: p.bezeichnung,
          provider: p.provider,
          host: p.host,
          port: p.port,
          benutzer: p.benutzer,
          passwort: p.passwort || '***', // "***" = Passwort unverändert lassen
          ordner: p.ordner,
          aktiv: !p.aktiv,
        },
        { headers }
      );
      load();
    } catch (e: any) {
      setAktionError(e.response?.data?.detail || 'Aktualisieren fehlgeschlagen');
    }
  };

  const loeschen = async (p: Postfach) => {
    if (!window.confirm(`Postfach "${p.bezeichnung}" wirklich löschen?`)) return;
    setAktionError(null);
    try {
      await axios.delete(`${API_URL}/api/postfaecher/${p.id}`, { headers });
      if (editId === p.id) formularSchliessen();
      load();
    } catch (e: any) {
      setAktionError(e.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  const testen = async (p: Postfach) => {
    setTestingId(p.id);
    setTestErgebnis(prev => {
      const next = { ...prev };
      delete next[p.id];
      return next;
    });
    try {
      const res = await axios.post(`${API_URL}/api/postfaecher/${p.id}/test`, null, { headers });
      setTestErgebnis(prev => ({
        ...prev,
        [p.id]: { ok: res.data.ok, text: res.data.detail || (res.data.ok ? 'Verbindung erfolgreich' : 'Verbindung fehlgeschlagen') },
      }));
      load(); // letzte_pruefung / letzter_status aktualisieren
    } catch (e: any) {
      setTestErgebnis(prev => ({
        ...prev,
        [p.id]: { ok: false, text: e.response?.data?.detail || 'Verbindung fehlgeschlagen' },
      }));
    } finally {
      setTestingId(null);
    }
  };

  const statusFarbe = (status: string | null) => {
    if (!status) return 'var(--ink-2)';
    return status.toLowerCase().startsWith('ok') ? 'var(--green-500)' : 'var(--red)';
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '6px 10px',
    border: '1px solid var(--bone-3)',
    borderRadius: 0,
    fontSize: 13,
    background: 'var(--bone)',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 13,
    fontWeight: 500,
    marginBottom: 4,
  };

  const kleinerButton = (aktivStil: boolean): React.CSSProperties => ({
    padding: '4px 12px',
    fontSize: 12,
    border: '1px solid var(--bone-3)',
    borderRadius: 0,
    background: 'var(--bone-2)',
    cursor: 'pointer',
    color: aktivStil ? 'inherit' : 'var(--red)',
  });

  return (
    <div style={{
      background: 'var(--bone)',
      border: '1px solid var(--bone-3)',
      borderRadius: 0,
      marginBottom: 24,
      overflow: 'hidden',
    }}>
      {/* Karten-Header (klickbar = auf-/zuklappen) */}
      <div
        onClick={onToggle}
        style={{
          background: 'var(--bone-2)',
          borderBottom: istOffen ? '1px solid var(--bone-3)' : 'none',
          padding: '12px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <span style={{ fontSize: 11, color: 'var(--ink-2)', marginTop: 4, userSelect: 'none' }}>
            {istOffen ? '▾' : '▸'}
          </span>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
              Überwachte Postfächer
              {postfaecher.length > 0 && (
                <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: 'var(--ink-2)' }}>
                  · {postfaecher.length} {postfaecher.length === 1 ? 'Eintrag' : 'Einträge'}
                </span>
              )}
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--ink-2)', fontWeight: 400 }}>
              Mehrere E-Mail-Postfächer überwachen. Sobald hier Einträge existieren, ersetzt diese Liste
              das Einzelpostfach aus den Gruppen IMAP bzw. GRAPH.
            </p>
          </div>
        </div>
        <button
          onClick={e => { e.stopPropagation(); if (!istOffen) onToggle(); neuesPostfach(); }}
          style={{
            padding: '5px 16px', fontSize: 13, fontWeight: 600,
            background: 'var(--green-500)', color: 'var(--bone)',
            border: 'none', borderRadius: 0, cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          + Postfach
        </button>
      </div>

      {istOffen && (
        <>
          {aktionError && (
            <div style={{
              padding: '8px 20px',
              background: 'var(--bone-2)',
              borderBottom: '1px solid var(--bone-3)',
              fontSize: 13,
              color: 'var(--red)',
            }}>
              ✗ {aktionError}
            </div>
          )}

          {loading ? (
            <div style={{ padding: '16px 20px', color: 'var(--ink-2)', fontSize: 13 }}>
              Lädt Postfächer...
            </div>
          ) : ladeError ? (
            <div style={{ padding: '16px 20px' }}>
              <p style={{ margin: '0 0 12px', color: 'var(--red)', fontSize: 14 }}>{ladeError}</p>
              <button onClick={load} style={{ padding: '6px 16px', background: 'var(--red)', color: 'var(--bone)', border: 'none', borderRadius: 0, cursor: 'pointer', fontSize: 13 }}>
                Erneut versuchen
              </button>
            </div>
          ) : (
            <>
              {/* Liste */}
              {postfaecher.length === 0 && !formOffen && (
                <div style={{ padding: '16px 20px', fontSize: 13, color: 'var(--ink-2)' }}>
                  Noch keine Postfächer — es gilt die Einzelpostfach-Konfiguration aus den
                  Gruppen IMAP bzw. GRAPH unten.
                </div>
              )}

              {postfaecher.length > 0 && (
                <div style={{ padding: '8px 20px' }}>
                  {postfaecher.map(p => {
                    const test = testErgebnis[p.id];
                    return (
                      <div key={p.id} style={{
                        padding: '10px 0',
                        borderBottom: '1px solid var(--bone-2)',
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: 16,
                          flexWrap: 'wrap',
                        }}>
                          <div style={{ minWidth: 220 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontWeight: 500, fontSize: 14, opacity: p.aktiv ? 1 : 0.5 }}>
                                {p.bezeichnung}
                              </span>
                              <span style={{
                                padding: '1px 8px',
                                fontSize: 11,
                                fontWeight: 600,
                                borderRadius: 0,
                                background: 'var(--bone-2)',
                                border: '1px solid var(--bone-3)',
                                color: p.provider === 'graph' ? 'var(--gold)' : 'var(--green-900)',
                              }}>
                                {p.provider === 'graph' ? 'Graph' : 'IMAP'}
                              </span>
                              {!p.aktiv && (
                                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--gold)' }}>
                                  inaktiv
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--ink-2)', fontFamily: 'monospace', marginTop: 2 }}>
                              {p.benutzer || '—'}
                              {p.provider === 'imap' && p.host ? ` @ ${p.host}:${p.port ?? 993}` : ''}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--ink-2)', marginTop: 2 }}>
                              {p.letzte_pruefung ? (
                                <>
                                  Letzte Prüfung: {new Date(p.letzte_pruefung).toLocaleString('de-DE')}
                                  {p.letzter_status && (
                                    <span style={{ color: statusFarbe(p.letzter_status), marginLeft: 6 }}>
                                      {p.letzter_status}
                                    </span>
                                  )}
                                </>
                              ) : (
                                'Noch nicht geprüft'
                              )}
                            </div>
                          </div>

                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                            {/* aktiv-Toggle */}
                            <label
                              style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer', userSelect: 'none' }}
                              title={p.aktiv ? 'Postfach deaktivieren' : 'Postfach aktivieren'}
                            >
                              <input
                                type="checkbox"
                                checked={p.aktiv}
                                onChange={() => toggleAktiv(p)}
                                style={{ cursor: 'pointer' }}
                              />
                              aktiv
                            </label>
                            <button
                              onClick={() => testen(p)}
                              disabled={testingId === p.id}
                              style={{
                                padding: '4px 12px', fontSize: 12,
                                background: 'var(--green-900)', color: 'var(--bone)',
                                border: 'none', borderRadius: 0, cursor: 'pointer',
                              }}
                            >
                              {testingId === p.id ? 'Teste...' : 'Verbindung testen'}
                            </button>
                            <button onClick={() => bearbeiten(p)} style={kleinerButton(true)}>
                              Bearbeiten
                            </button>
                            <button onClick={() => loeschen(p)} style={kleinerButton(false)}>
                              Löschen
                            </button>
                          </div>
                        </div>

                        {/* Testergebnis inline */}
                        {test && (
                          <div style={{
                            marginTop: 6,
                            fontSize: 12,
                            color: test.ok ? 'var(--green-500)' : 'var(--red)',
                          }}>
                            {test.ok ? '✓' : '✗'} {test.text}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Formular Anlegen / Bearbeiten */}
              {formOffen && (
                <div style={{
                  padding: '16px 20px',
                  borderTop: postfaecher.length > 0 ? '1px solid var(--bone-3)' : 'none',
                  background: 'var(--bone)',
                }}>
                  <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>
                    {editId != null ? 'Postfach bearbeiten' : 'Neues Postfach'}
                  </h4>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 12,
                    marginBottom: 12,
                  }}>
                    <div>
                      <label style={labelStyle}>Bezeichnung</label>
                      <input
                        type="text"
                        value={form.bezeichnung}
                        onChange={e => handleForm('bezeichnung', e.target.value)}
                        placeholder="z.B. Rechnungseingang"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Provider</label>
                      <select
                        value={form.provider}
                        onChange={e => handleForm('provider', e.target.value)}
                        style={{ ...inputStyle, cursor: 'pointer' }}
                      >
                        <option value="imap">IMAP</option>
                        <option value="graph">Microsoft 365 (Graph)</option>
                      </select>
                    </div>

                    {form.provider === 'imap' ? (
                      <>
                        <div>
                          <label style={labelStyle}>Host</label>
                          <input
                            type="text"
                            value={form.host}
                            onChange={e => handleForm('host', e.target.value)}
                            placeholder="z.B. imap.firma.de"
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label style={labelStyle}>Port</label>
                          <input
                            type="number"
                            value={form.port}
                            onChange={e => handleForm('port', e.target.value)}
                            placeholder="993"
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label style={labelStyle}>Benutzer</label>
                          <input
                            type="text"
                            value={form.benutzer}
                            onChange={e => handleForm('benutzer', e.target.value)}
                            placeholder="z.B. rechnung@firma.de"
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label style={labelStyle}>Passwort</label>
                          <input
                            type="password"
                            value={form.passwort}
                            onChange={e => handleForm('passwort', e.target.value)}
                            placeholder={editId != null ? '*** = unverändert lassen' : ''}
                            style={{ ...inputStyle, fontFamily: 'monospace' }}
                          />
                        </div>
                        <div>
                          <label style={labelStyle}>Ordner</label>
                          <input
                            type="text"
                            value={form.ordner}
                            onChange={e => handleForm('ordner', e.target.value)}
                            placeholder="INBOX"
                            style={inputStyle}
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <label style={labelStyle}>Postfach-Adresse</label>
                          <input
                            type="text"
                            value={form.benutzer}
                            onChange={e => handleForm('benutzer', e.target.value)}
                            placeholder="z.B. info@firma.de"
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label style={labelStyle}>Ordner</label>
                          <input
                            type="text"
                            value={form.ordner}
                            onChange={e => handleForm('ordner', e.target.value)}
                            placeholder="inbox"
                            style={inputStyle}
                          />
                        </div>
                      </>
                    )}
                  </div>

                  {form.provider === 'graph' && (
                    <p style={{ fontSize: 12, color: 'var(--ink-2)', margin: '0 0 12px' }}>
                      Die Azure-Zugangsdaten (Tenant, Client-ID, Client-Secret) werden aus der
                      Gruppe "E-Mail über Microsoft 365 (Graph API)" unten verwendet.
                    </p>
                  )}

                  {formError && (
                    <p style={{ fontSize: 13, color: 'var(--red)', margin: '0 0 12px' }}>
                      ✗ {formError}
                    </p>
                  )}

                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={speichern}
                      disabled={saving}
                      style={{
                        padding: '8px 20px', fontSize: 13, fontWeight: 600,
                        background: 'var(--green-500)', color: 'var(--bone)',
                        border: 'none', borderRadius: 0,
                        cursor: saving ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {saving ? 'Speichert...' : (editId != null ? 'Änderungen speichern' : 'Postfach anlegen')}
                    </button>
                    <button
                      onClick={formularSchliessen}
                      disabled={saving}
                      style={{
                        padding: '8px 20px', fontSize: 13,
                        background: 'var(--bone-2)', color: 'inherit',
                        border: '1px solid var(--bone-3)', borderRadius: 0,
                        cursor: 'pointer',
                      }}
                    >
                      Abbrechen
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default PostfachSettings;
